import React, { createContext, useContext, useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import { shortsApi, vodApi } from "@shared/apis"
import {
  getShortsDetailErrorMessage,
  getShortsValidateErrorMessage,
  hasShortsPermission,
  setAnonymousShortsTokens,
  SHORTS_NO_PERMISSION_MESSAGE,
  ShortsDetail,
  ShortsValidateResult,
  validateShortsEntry,
} from "@shared/apis/shorts"
import { Status } from "@shared/constants"
import { useSchedule } from "@shared/contexts"
import { kick, redirectLoginUrl } from "@shared/modules"

const vodsContext = createContext<Vod[]>([])
const selectedVodContext = createContext<Vod | null>(null)
const shortsDetailContext = createContext<ShortsDetail | null>(null)
const setSelectedVodContext = createContext<
  React.Dispatch<React.SetStateAction<Vod | null>>
>(() => {})

interface Props {
  children: React.ReactNode
}

export function VodsProvider({ children }: Props) {
  const { schedule } = useSchedule()
  const { shortsId } = useParams<{ shortsId?: string }>()

  const [vods, setVods] = useState<Vod[]>([])
  const [selectedVod, setSelectedVod] = useState<Vod | null>(null)
  const [shortsDetail, setShortsDetail] = useState<ShortsDetail | null>(null)

  useEffect(() => {
    if (!schedule) return

    // 숏폼 재생: 와우넷 진입 검증(validateEntry)을 통과해야만 실제 재생 정보(getShortsDetail)를 조회한다.
    // 두 호출을 분리해서 동시에 실행하면 검증 실패 처리 전에 영상이 먼저 노출될 수 있어 반드시 순서를 묶는다.
    if (shortsId) {
      const loadShorts = async () => {
        let entry: ShortsValidateResult | null

        try {
          entry = await validateShortsEntry(Number(shortsId), {
            withTicket: true,
          })
        } catch (err) {
          kick(getShortsValidateErrorMessage(err))
          return
        }

        // 에러 없이 200이어도 권한이 없으면 UNAUTH가 내려온다
        if (!hasShortsPermission(entry)) {
          kick(SHORTS_NO_PERMISSION_MESSAGE)
          return
        }

        // 비회원은 check API로 토큰을 받지 못하므로 여기서 받은 토큰을 세팅한다.
        // getShortsDetail이 이 토큰을 쓰기 때문에 반드시 먼저 세팅되어야 한다.
        setAnonymousShortsTokens(entry)

        try {
          const { data: short } = await shortsApi.getShortsDetail(
            Number(shortsId),
          )
          const shortAsVod: Vod = {
            id: short.id!,
            streamUrl: short.streamUrl,
            isEnable: true, // 실제로 안 쓰임, 타입위해 들어감
            vodType: "NORMAL", // 실제로 안 쓰임, 타입위해 들어감
            recordStartDate: short.recordStartDate ?? "",
            recordEndDate: short.recordEndDate ?? "",
            vodHost: "", // 실제로 안 쓰임, 타입위해 들어감
            middlePath: "", // 실제로 안 쓰임, 타입위해 들어감
            fileName: short.fileName ?? "",
          }

          setShortsDetail(short)
          setVods([shortAsVod])
          setSelectedVod(shortAsVod)
        } catch (err) {
          // 진입 검증을 통과한 뒤의 실패라 원인이 달라 문구를 구분한다.
          kick(getShortsDetailErrorMessage(err))
        }
      }

      loadShorts()
      return
    }

    const vodType: VodType =
      schedule?.progressStatus === "COMPLETED" ? "LONG" : "NORMAL"

    vodApi
      .get(schedule.id, vodType)
      .then(({ data: vods }) => {
        if (!vods.content.length) {
          kick("등록된 VOD가 없습니다.")
          return
        }

        vods.content.sort(
          (prevVod: Vod, currentVod: Vod) => prevVod.id - currentVod.id,
        )

        const { 0: firstVod } = vods.content
        setVods(vods.content)
        setSelectedVod(firstVod)
      })
      .catch(({ response }) => {
        const { data, status } = response
        let message = data
        if (status === Status.NOT_FOUND) {
          message = "잘못된 VOD입니다"
        }
        if (status === Status.ACCESS_DENIED) {
          const scheduleId = schedule.id
          return redirectLoginUrl(scheduleId)
        }
        kick(message)
      })
  }, [schedule, shortsId])

  return (
    <vodsContext.Provider value={vods}>
      <selectedVodContext.Provider value={selectedVod}>
        <setSelectedVodContext.Provider value={setSelectedVod}>
          <shortsDetailContext.Provider value={shortsDetail}>
            {children}
          </shortsDetailContext.Provider>
        </setSelectedVodContext.Provider>
      </selectedVodContext.Provider>
    </vodsContext.Provider>
  )
}

export function useVods() {
  return useContext(vodsContext)
}

export function useSelectedVod() {
  return useContext(selectedVodContext)
}

export function useShortsDetail() {
  return useContext(shortsDetailContext)
}

export function useSetSelectedVod() {
  return useContext(setSelectedVodContext)
}

export interface Vod {
  id: number
  streamUrl: string
  isEnable: boolean
  vodType: VodType
  recordStartDate: string
  recordEndDate: string
  vodHost: string
  middlePath: string
  fileName: string
}

export type VodType = "NORMAL" | "LONG" | "STOCK"
